import { createAvroEventsHub } from "@zapier/avro-events";
import type {
  EventArgs,
  EventFieldsSetByAPI,
  EventFieldsSetByEventsToolkit,
  EventNameType,
  WebAnalyticsTrackingUserInteractionUserInteractionEvent,
} from "@zapier/avro-events";

import { emit } from "@zapier/events-toolkit-browser";
import type { WebMcpToolOutcome } from "src/utils/webmcp";
import {
  UserInteractionEvent,
  UserInteractionEventName,
} from "./trackingTypes";

const SYSTEM_NAME = "templates";

type RemoveAutoPopulatedEventFields<Type> = {
  [
    Property in keyof Type as Exclude<
      Property,
      EventFieldsSetByAPI | EventFieldsSetByEventsToolkit
    >
  ]: Type[Property];
};

const emitEvent = <T extends EventNameType>(
  eventName: T | UserInteractionEventName,
  customEventData?: RemoveAutoPopulatedEventFields<EventArgs<T>>,
  systemName?: string,
) => {
  return createAvroEventEmitter(eventName as EventNameType)({
    system_name: systemName || SYSTEM_NAME,
    ...(customEventData || {}),
  });
};

export default emitEvent;

export const emitUserInteractionEvent = (
  name: UserInteractionEventName,
  properties: Omit<
    RemoveAutoPopulatedEventFields<WebAnalyticsTrackingUserInteractionUserInteractionEvent.EventFields>,
    "system_name"
  >,
) => {
  const payload = {
    ...properties,
    interaction_name: name,
  };
  return createAvroEventEmitter(UserInteractionEvent)(payload);
};

/**
 * Emit a telemetry event for a single WebMCP tool invocation. Matches the v2
 * cross-surface contract (explore's `/mcp/:app`, app directory, …) so the Agent
 * Discovery Funnel dashboard (AGD-13) can count invocations across repos with one
 * query: `webmcp_tool_invoked` / `agent_tool_invocation`, tool name in
 * `option_selected`, outcome + surface in `custom_tracking_fields`.
 *
 * The imperative WebMCP API has no `toolactivated`/`toolcancel` events, so we
 * instrument the invocation itself (success vs. error). `event_action: "view"`
 * reflects that these tools are read-only. Pass `extraFields` for surface-specific
 * context.
 */
export const emitWebMcpToolInvocationEvent = (
  surface: string,
  toolName: string,
  outcome: WebMcpToolOutcome,
  extraFields: Record<string, string> = {},
) => {
  return emitUserInteractionEvent(UserInteractionEventName.webmcpToolInvoked, {
    event_action: "view",
    interaction_goal: "agent_tool_invocation",
    option_selected: toolName,
    custom_tracking_fields: {
      webmcp_tool_name: toolName,
      webmcp_tool_outcome: outcome,
      webmcp_surface: surface,
      ...extraFields,
    },
  });
};

export type AvroEventEmitter<T extends EventNameType> = (
  data: Omit<RemoveAutoPopulatedEventFields<EventArgs<T>>, "system_name">,
) => Promise<void>;

function createAvroEventEmitter<T extends EventNameType>(
  eventName: T,
): AvroEventEmitter<T> {
  const event = createAvroEventsHub<T>();
  event.emitFunction = emit;

  return async function emitEvent(data) {
    // We can't seem to convince TypeScript that `Omit`ing "system_name" then
    // adding it back in is a safe operation. It obviously is, though, so I'm
    // forcing this code with an `as any` to make it work.
    const fullData: RemoveAutoPopulatedEventFields<EventArgs<T>> = {
      system_name: SYSTEM_NAME,
      ...data,
    } as any;

    event.emit(fullData, eventName);
  };
}
